import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ListFilter } from 'lucide-react';

import { Badge } from '@/components/Badge/Badge';
import { Button } from '@/components/Button/Button';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { FilterDrawer } from '@/components/FilterDrawer/FilterDrawer';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { ORDER_STATUS, PAYMENT_STATUS } from '@/features/orders/orders';
import { orderLookupsQuery } from '@/features/orders/queries';
import {
    orderQueueExportUrl,
    type OrderQueue,
    type OrderQueueParams,
    type OrderQueueRow,
} from '@/features/reports/api';
import { TRANSACTIONS_COPY } from '@/features/reports/copy';
import { formatPounds } from '@/features/reports/format';
import { ReportCard, ReportFailure } from '@/features/reports/parts';
import { orderQueueQuery } from '@/features/reports/queries';
import { QUEUE_BUCKET, QUEUE_TILES, type QueueBucket } from '@/features/reports/tabs';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { exportFileName, nextAction } from '@/features/reports/transactions/labels';
import {
    appliedFromQueue,
    queueFilterCount,
    queueFilterGroups,
    queueFromApplied,
} from '@/features/reports/transactions/queueFilters';
import { cn } from '@/util/utils';

const COPY = TRANSACTIONS_COPY.orders;

/**
 * Transactions › Orders (ALS-86): the prototype's action queue over the legacy Transactions ›
 * Order report. The stat cards are the primary filter, not decoration: a queue is worked bucket by
 * bucket, so a card narrows to its bucket and a second press clears it. The cards count under
 * every other filter, so they never contradict the table beneath them.
 */
export function OrderQueueView({
    params,
    onChange,
}: {
    params: OrderQueueParams;
    onChange: (params: OrderQueueParams) => void;
}) {
    const queue = useQuery(orderQueueQuery(params));
    const lookups = useQuery(orderLookupsQuery);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const asked = params.q ?? '';
    const [search, setSearch] = useState(asked);
    const [synced, setSynced] = useState(asked);
    const data = queue.data;

    // The URL is the source of truth: Back, Forward or a shared link rewrites the box. The search
    // this box just sent comes back trimmed, and is left alone so a trailing space survives typing.
    if (asked !== synced) {
        setSynced(asked);
        if (asked !== search.trim()) setSearch(asked);
    }

    // The search waits for a pause in typing, so each keystroke is not a request.
    useEffect(() => {
        const typed = search.trim();
        if (typed === asked) return;
        const wait = window.setTimeout(() => onChange({ ...params, q: typed || undefined }), 300);
        return () => window.clearTimeout(wait);
    }, [search, asked, params, onChange]);

    const filterCount = queueFilterCount(params);

    return (
        <>
            {queue.isPending ? (
                <QueueSkeleton />
            ) : queue.isError && !data ? (
                <ReportFailure
                    copy={TRANSACTIONS_COPY.failed}
                    onRetry={() => void queue.refetch()}
                />
            ) : data ? (
                <>
                    <BucketTiles
                        queue={data}
                        selected={params.bucket}
                        onSelect={(bucket) => onChange({ ...params, bucket })}
                    />
                    <ReportCard
                        title={COPY.card}
                        subtitle={COPY.count(data.rows.length, data.total)}
                        actions={
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setFiltersOpen(true)}
                                >
                                    <ListFilter />
                                    {filterCount > 0
                                        ? `${COPY.filters} · ${filterCount}`
                                        : COPY.filters}
                                </Button>
                                <ExportButton
                                    url={orderQueueExportUrl(params)}
                                    fileName={exportFileName(['orders'])}
                                    disabled={data.rows.length === 0}
                                />
                            </>
                        }
                    >
                        <div className="pb-3">
                            <SearchInput
                                aria-label={COPY.search}
                                placeholder={COPY.search}
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                className="sm:max-w-xs"
                            />
                        </div>
                        {data.rows.length === 0 ? (
                            <EmptyState
                                icon={<ListFilter />}
                                title={COPY.empty.title}
                                body={COPY.empty.body}
                            />
                        ) : (
                            <QueueTable rows={data.rows} />
                        )}
                    </ReportCard>
                </>
            ) : null}

            {filtersOpen && lookups.data ? (
                <FilterDrawer
                    open
                    onOpenChange={setFiltersOpen}
                    groups={queueFilterGroups(lookups.data)}
                    applied={appliedFromQueue(params)}
                    onApply={(applied) => {
                        setFiltersOpen(false);
                        onChange(queueFromApplied(applied, { q: params.q, bucket: params.bucket }));
                    }}
                    onClearAll={() => {
                        setFiltersOpen(false);
                        setSearch('');
                        onChange({});
                    }}
                />
            ) : null}
        </>
    );
}

function BucketTiles({
    queue,
    selected,
    onSelect,
}: {
    queue: OrderQueue;
    selected: QueueBucket | undefined;
    onSelect: (bucket: QueueBucket | undefined) => void;
}) {
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {QUEUE_TILES.map((bucket) => {
                const on = selected === bucket;
                const count = queue.counts.find((c) => c.bucket === bucket)?.count ?? 0;
                return (
                    <button
                        key={bucket}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onSelect(on ? undefined : bucket)}
                        className={cn(
                            'bg-card border-border min-h-11 rounded-lg border px-4 py-3 text-left',
                            'focus-visible:outline-ring outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
                            on && 'border-primary ring-primary ring-1',
                        )}
                    >
                        <span className="block text-2xl font-semibold tracking-tight">{count}</span>
                        <span className="text-label-md text-muted-foreground mt-0.5 block">
                            {COPY.buckets[bucket]}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function QueueTable({ rows }: { rows: OrderQueueRow[] }) {
    const C = COPY.columns;
    return (
        // On a phone each order is a card: its number leads with what it needs next under it, as a
        // queue is scanned for, and the rest are labelled lines beneath. The next action is not the
        // card's trailing slot: an order number does not wrap, and the two ran into each other.
        <Table stack region={COPY.card}>
            <TableHeader>
                <TableRow>
                    <TableHead>{C.order}</TableHead>
                    <TableHead>{C.client}</TableHead>
                    <TableHead>{C.rm}</TableHead>
                    <TableHead className="text-right">{C.value}</TableHead>
                    <TableHead>{C.status}</TableHead>
                    <TableHead>{C.payment}</TableHead>
                    <TableHead>{C.next}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.orderId}>
                        <TableCell data-stack="title" className="font-medium">
                            <div className="flex min-w-0 flex-col gap-[3px]">
                                <Link
                                    to="/orders/$orderId"
                                    params={{ orderId: String(row.orderId) }}
                                    className="whitespace-nowrap hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                                >
                                    {row.orderNo}
                                </Link>
                                <NextAction row={row} className="md:hidden" />
                            </div>
                        </TableCell>
                        <TableCell
                            data-stack="meta"
                            data-label={C.client}
                            className="md:whitespace-nowrap"
                        >
                            {row.client}
                        </TableCell>
                        <TableCell
                            data-stack="meta"
                            data-label={C.rm}
                            className="text-muted-foreground md:whitespace-nowrap"
                        >
                            {row.relationshipManager ?? ''}
                        </TableCell>
                        <TableNum data-stack="meta" data-label={C.value}>
                            {formatPounds(row.value)}
                        </TableNum>
                        <TableCell data-stack="meta" data-label={C.status}>
                            <Badge size="sm" variant={ORDER_STATUS[row.status].variant}>
                                {ORDER_STATUS[row.status].label}
                            </Badge>
                        </TableCell>
                        <TableCell data-stack="meta" data-label={C.payment}>
                            <Badge size="sm" variant={PAYMENT_STATUS[row.payment].variant}>
                                {PAYMENT_STATUS[row.payment].label}
                            </Badge>
                        </TableCell>
                        <TableCell data-stack="hide">
                            <NextAction row={row} />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function NextAction({ row, className }: { row: OrderQueueRow; className?: string }) {
    return (
        <span
            className={cn(
                'whitespace-nowrap',
                row.bucket === QUEUE_BUCKET.Overdue && 'text-destructive font-medium',
                className,
            )}
        >
            {nextAction(row)}
        </span>
    );
}

function QueueSkeleton() {
    return (
        <div
            aria-busy="true"
            aria-label="Loading the order queue"
            className="flex flex-col gap-(--layout-row-gap)"
        >
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {Array.from({ length: 4 }, (_, i) => (
                    <Skeleton key={i} className="h-[76px] rounded-lg" />
                ))}
            </div>
            <Skeleton className="h-[320px] rounded-md" />
        </div>
    );
}
